import { Controller } from '@hotwired/stimulus';
import { getDroppedFiles } from '@uppy/utils';
import { post } from '@rails/request.js';
import { uploadFiles, MINT_BATCH_MAX_FILES } from '../vdr/azure_sas_uploader.js';
import { hashBlob, HASH_CHUNK_SIZE } from '../vdr/sha256.js';

export default class extends Controller {
  static targets = [
    'dropzone',
    'input',
    'idlePanel',
    'progressWrapper',
    'progressPanel',
    'progressTotalLabel',
    'progressList',
    'explorerWrapper',
    'explorerBody',
    'explorerPanel',
    'explorerBreadcrumb',
    'uploadLabel',
    'uploadingLabel',
    'downloadAllButton',
    'actions',
    'seedData',
    'duplicateModal',
    'duplicateTitle',
    'duplicateMessage',
    'duplicateList',
    'duplicateConfirm',
    'duplicateCancel',
  ];

  static values = {
    createUrl: String,
    diffUrl: String,
    confirmUrl: String,
    wipeUrl: String,
    locking: Boolean,
    noFolderText: String,
    verifyingFilesText: String,
    ofFilesText: String,
    errorText: String,
    fileIconUrl: String,
    checkIconUrl: String,
    folderLabel: String,
    rootLabel: String,
    backLabel: String,
    skipTitle: String,
    skipBody: String,
    overwriteTitle: String,
    overwriteBody: String,
    overwriteAndSkipBody: String,
    overwriteConfirm: String,
    continueText: String,
    cancelText: String,
    willOverwriteText: String,
    willSkipText: String,
    noneTitle: String,
    noneBody: String,
    closeText: String,
  };

  connect() {
    this.uploading = false;
    this.uploadLocked = false;
    this.pendingItems = null;
    this.downloadAllWasVisible = false;
    this.entries = this.readSeedEntries();
    this.currentPath = '';
    this.boundBeforeUnload = this.handleBeforeUnload.bind(this);
    this.boundPageHide = this.handlePageHide.bind(this);
    window.addEventListener('beforeunload', this.boundBeforeUnload);
    window.addEventListener('pagehide', this.boundPageHide);

    if (this.entries.length > 0) this.renderExplorer();
  }

  disconnect() {
    window.removeEventListener('beforeunload', this.boundBeforeUnload);
    window.removeEventListener('pagehide', this.boundPageHide);
  }

  readSeedEntries() {
    if (!this.hasSeedDataTarget) return [];

    try {
      const raw = JSON.parse(this.seedDataTarget.textContent || '[]');
      if (!Array.isArray(raw)) return [];

      return raw.map((file) => ({
        path: file.path,
        name: this.basename(file.path),
        size: file.size,
        uploadedAt: file.uploaded || '',
      }));
    } catch (_error) {
      return [];
    }
  }

  handleBeforeUnload(event) {
    if (!this.uploading) return;
    event.preventDefault();
    event.returnValue = '';
  }

  handlePageHide() {
    if (!this.uploading || !this.wipeUrlValue) return;

    const token = document.querySelector("meta[name='csrf-token']")?.content || '';
    fetch(this.wipeUrlValue, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-Token': token,
      },
      body: '{}',
      credentials: 'same-origin',
      keepalive: true,
    }).catch(() => {});
  }

  openPicker() {
    if (this.uploading || this.uploadLocked || !this.hasInputTarget) return;
    this.inputTarget.click();
  }

  inputChanged(event) {
    if (this.uploadLocked) {
      event.target.value = '';
      return;
    }

    const items = Array.from(event.target.files || [])
      .filter((file) => file.webkitRelativePath)
      .filter((file) => !this.isHiddenPath(file.webkitRelativePath))
      .map((file) => ({ relativePath: file.webkitRelativePath, file }));

    event.target.value = '';
    this.start(items);
  }

  dragOver(event) {
    event.preventDefault();
    if (this.uploadLocked || this.uploading || !this.hasDropzoneTarget) return;
    this.dropzoneTarget.dataset.dragging = 'true';
  }

  dragLeave(event) {
    event.preventDefault();
    if (!this.hasDropzoneTarget) return;
    if (event.relatedTarget && this.dropzoneTarget.contains(event.relatedTarget)) return;
    delete this.dropzoneTarget.dataset.dragging;
  }

  async dropped(event) {
    event.preventDefault();
    if (this.hasDropzoneTarget) delete this.dropzoneTarget.dataset.dragging;
    if (this.uploading || this.uploadLocked) return;

    const files = await getDroppedFiles(event.dataTransfer);
    const items = files
      .filter((file) => file.relativePath)
      .map((file) => ({ relativePath: file.relativePath.replace(/^\/+/, ''), file }))
      .filter((item) => !this.isHiddenPath(item.relativePath));

    if (items.length === 0) {
      window.alert(this.noFolderTextValue);
      return;
    }

    this.start(items);
  }

  isHiddenPath(relativePath) {
    return String(relativePath || '')
      .split('/')
      .some((segment) => segment.startsWith('.'));
  }

  async start(items) {
    if (this.uploading || this.uploadLocked || items.length === 0) return;

    this.uploading = true;
    this.pendingItems = null;
    this.showProgressUi(items, 'verifying');

    try {
      const hashed = await this.hashItems(items);
      const diff = await this.fetchDiff(hashed);

      const uploadPaths = new Set(
        diff.files.filter((file) => file.action === 'upload').map((file) => file.relative_path)
      );
      const toUpload = hashed.filter((item) => uploadPaths.has(item.relativePath));
      const skipped = diff.files.filter((file) => file.action === 'skip');
      const overwrites = diff.files.filter((file) => file.action === 'upload' && file.overwrite);

      if (skipped.length > 0 || overwrites.length > 0) {
        this.pendingItems = toUpload;
        this.hideTarget('progressWrapper');
        this.showDuplicateModal({ skipped, overwrites, toUploadCount: toUpload.length });
        return;
      }

      if (toUpload.length === 0) {
        this.uploading = false;
        this.showIdleUi();
        return;
      }

      this.setProgressPhase('uploading', 0, toUpload.length);
      await this.performUpload(toUpload);
    } catch (_error) {
      window.alert(this.errorTextValue || 'Upload failed. Please try again.');
      this.showIdleUi();
    } finally {
      if (!this.pendingItems) this.uploading = false;
    }
  }

  async fetchDiff(hashed) {
    const manifest = hashed.map((item) => ({
      relative_path: item.relativePath,
      mime_type: item.file.type || '',
      sha256: item.sha256,
      size: item.file.size,
    }));

    const files = [];
    let changedMode = null;

    for (let index = 0; index < manifest.length; index += MINT_BATCH_MAX_FILES) {
      const response = await post(this.diffUrlValue, {
        body: JSON.stringify({ files: manifest.slice(index, index + MINT_BATCH_MAX_FILES) }),
        contentType: 'application/json',
        responseKind: 'json',
      });

      if (!response.ok) throw new Error(`diff failed (${response.status})`);

      const payload = await response.json;
      files.push(...(payload.files || []));
      if (changedMode === null) changedMode = payload.changed_mode;
    }

    return { changed_mode: changedMode, files };
  }

  async hashItems(items) {
    const hashed = [];

    for (const item of items) {
      const current = hashed.length + 1;
      console.log(`[vdr-upload] hashing ${current}/${items.length}: ${item.relativePath}`);
      this.setProgressPhase('verifying', current, items.length);
      const sha256 = await hashBlob(item.file, HASH_CHUNK_SIZE);
      hashed.push({ relativePath: item.relativePath, file: item.file, sha256 });
    }

    return hashed;
  }

  showDuplicateModal({ skipped, overwrites, toUploadCount }) {
    const skipCount = skipped.length;
    const overwriteCount = overwrites.length;

    let title;
    let body;
    let confirmLabel;
    let cancelLabel = this.cancelTextValue;
    let showConfirm = true;

    if (toUploadCount === 0) {
      title = this.noneTitleValue;
      body = this.interpolate(this.noneBodyValue, { count: skipCount });
      cancelLabel = this.closeTextValue || this.cancelTextValue;
      showConfirm = false;
    } else if (overwriteCount > 0 && skipCount > 0) {
      title = this.overwriteTitleValue;
      body = this.interpolate(this.overwriteAndSkipBodyValue, {
        overwrite: overwriteCount,
        skip: skipCount,
      });
      confirmLabel = this.overwriteConfirmValue;
    } else if (overwriteCount > 0) {
      title = this.overwriteTitleValue;
      body = this.interpolate(this.overwriteBodyValue, { count: overwriteCount });
      confirmLabel = this.overwriteConfirmValue;
    } else {
      title = this.skipTitleValue;
      body = this.interpolate(this.skipBodyValue, { count: skipCount });
      confirmLabel = this.continueTextValue;
    }

    if (this.hasDuplicateTitleTarget) this.duplicateTitleTarget.textContent = title || '';
    if (this.hasDuplicateMessageTarget) this.duplicateMessageTarget.textContent = body || '';

    this.renderDuplicateList([
      ...overwrites.map((file) => ({
        label: this.willOverwriteTextValue,
        path: file.relative_path,
      })),
      ...skipped.map((file) => ({ label: this.willSkipTextValue, path: file.relative_path })),
    ]);
    this.setModalButtons({ showConfirm, confirmLabel, cancelLabel });

    if (this.hasDuplicateModalTarget) this.duplicateModalTarget.classList.remove('hidden');
  }

  interpolate(template, values) {
    return Object.entries(values).reduce(
      (result, [key, value]) => result.replace(`%{${key}}`, String(value)),
      String(template || '')
    );
  }

  renderDuplicateList(entries) {
    if (!this.hasDuplicateListTarget) return;

    this.duplicateListTarget.innerHTML = (entries || [])
      .map(
        (entry) =>
          `<li class="text-xs text-gray-500 leading-5 break-all"><span class="font-semibold text-gray-600">${this.escapeHtml(
            entry.label
          )}:</span> ${this.escapeHtml(entry.path)}</li>`
      )
      .join('');
  }

  setModalButtons({ showConfirm, confirmLabel, cancelLabel }) {
    if (this.hasDuplicateCancelTarget && cancelLabel) {
      this.duplicateCancelTarget.textContent = cancelLabel;
    }

    if (!this.hasDuplicateConfirmTarget) return;

    if (showConfirm) {
      this.duplicateConfirmTarget.classList.remove('hidden');
      this.duplicateConfirmTarget.textContent = confirmLabel || this.continueTextValue || '';
    } else {
      this.duplicateConfirmTarget.classList.add('hidden');
    }
  }

  hideDuplicateModal() {
    if (this.hasDuplicateModalTarget) this.duplicateModalTarget.classList.add('hidden');
  }

  cancelDuplicateUpload() {
    this.pendingItems = null;
    this.hideDuplicateModal();
    this.uploading = false;
    this.showIdleUi();
  }

  async confirmDuplicateUpload() {
    const items = this.pendingItems;
    this.pendingItems = null;
    this.hideDuplicateModal();
    if (!items || items.length === 0) {
      this.uploading = false;
      this.showIdleUi();
      return;
    }

    this.uploading = true;
    this.showProgressUi(items, 'uploading');

    try {
      await this.performUpload(items);
    } catch (_error) {
      window.alert(this.errorTextValue || 'Upload failed. Please try again.');
      this.showIdleUi();
    } finally {
      this.uploading = false;
    }
  }

  async performUpload(items) {
    const byPath = new Map(items.map((item) => [item.relativePath, item]));
    const completed = [];

    this.setProgressPhase('uploading', 0, items.length);

    await uploadFiles({
      items,
      createUrl: this.createUrlValue,
      confirmUrl: this.confirmUrlValue,
      wipeUrl: this.wipeUrlValue,
      callbacks: {
        onProgress: (path, uploaded, total) => this.updateProgressBar(path, uploaded, total),
        onStatus: (path, status) => {
          if (status === 'uploading') {
            this.promoteInProgress(path);
            return;
          }
          if (status !== 'done') return;
          const item = byPath.get(path);
          if (item) completed.push(item);
          this.markProgressComplete(path);
          this.setProgressPhase('uploading', completed.length, items.length);
        },
      },
    });

    this.uploading = false;
    this.mergeEntries(completed);
    if (this.lockingValue) this.lockUpload();
    this.showExplorer();
    this.dispatch('uploaded', { target: window, prefix: 'vdr-upload' });
  }

  mergeEntries(items) {
    const uploadedAt = this.formatDate(new Date());
    const byPath = new Map(this.entries.map((entry) => [entry.path, entry]));

    items.forEach((item) => {
      const path = item.relativePath;
      byPath.set(path, { path, name: this.basename(path), size: item.file.size, uploadedAt });
    });

    this.entries = Array.from(byPath.values());
  }

  lockUpload() {
    this.uploadLocked = true;
    if (this.hasUploadLabelTarget) this.uploadLabelTarget.classList.add('hidden');
    if (this.hasUploadingLabelTarget) {
      this.uploadingLabelTarget.classList.add('hidden');
      this.uploadingLabelTarget.classList.remove('inline-flex');
    }
    if (this.hasInputTarget) {
      this.inputTarget.disabled = true;
      this.inputTarget.removeAttribute('id');
    }
    if (this.hasIdlePanelTarget) this.idlePanelTarget.classList.add('hidden');
  }

  showProgressUi(items, phase) {
    this.hideTarget('idlePanel');
    this.hideTarget('explorerWrapper');
    this.showTarget('progressWrapper');

    if (this.hasUploadLabelTarget) this.uploadLabelTarget.classList.add('hidden');
    if (this.hasUploadingLabelTarget) {
      this.uploadingLabelTarget.classList.remove('hidden');
      this.uploadingLabelTarget.classList.add('inline-flex');
    }
    if (this.hasDownloadAllButtonTarget) {
      this.downloadAllWasVisible = !this.downloadAllButtonTarget.classList.contains('hidden');
      this.downloadAllButtonTarget.classList.add('hidden');
    }

    this.setProgressPhase(phase, 0, items.length);
    this.renderProgressRows(items);
  }

  renderProgressRows(items) {
    if (!this.hasProgressListTarget) return;

    this.progressListTarget.innerHTML = items
      .map((item, index) => {
        const name = item.relativePath;
        const isLast = index === items.length - 1;
        const border = isLast ? '' : 'border-b border-gray-200';
        return `
          <div class="self-stretch pt-2.5 pb-6 ${border} inline-flex justify-start items-center gap-2.5" data-progress-row data-path="${this.escapeAttr(
          item.relativePath
        )}" data-index="${index}">
            <img class="size-6 shrink-0" src="${this.escapeAttr(this.fileIconUrlValue)}" alt="">
            <div class="flex-1 min-w-0 inline-flex flex-col justify-start items-start gap-2">
              <div class="self-stretch inline-flex justify-between items-center gap-2">
                <div class="justify-center text-gray-900 text-sm font-normal font-sans leading-4 break-all">${this.escapeHtml(
                  name
                )}</div>
                <img class="hidden size-5 shrink-0" src="${this.escapeAttr(
                  this.checkIconUrlValue
                )}" alt="" data-role="check">
              </div>
              <div class="self-stretch h-2.5 relative bg-gray-200 rounded-full overflow-hidden">
                <div class="absolute left-0 top-0 bottom-0 bg-brand-blue-0 rounded-l-full" data-role="bar" style="width: 0%"></div>
              </div>
            </div>
          </div>`;
      })
      .join('');
  }

  progressRow(path) {
    if (!this.hasProgressListTarget) return null;
    return this.progressListTarget.querySelector(
      `[data-progress-row][data-path="${CSS.escape(path)}"]`
    );
  }

  updateProgressBar(path, uploaded, total) {
    const bar = this.progressRow(path)?.querySelector('[data-role="bar"]');
    if (!bar || !total) return;
    bar.style.width = `${Math.min(100, Math.round((uploaded / total) * 100))}%`;
  }

  promoteInProgress(path) {
    const row = this.progressRow(path);
    if (!row || !this.hasProgressListTarget) return;

    if (this.progressListTarget.firstChild !== row) {
      this.progressListTarget.insertBefore(row, this.progressListTarget.firstChild);
    }
    this.refreshProgressBorders();
  }

  markProgressComplete(path) {
    const row = this.progressRow(path);
    if (!row) return;

    const bar = row.querySelector('[data-role="bar"]');
    if (bar) {
      bar.classList.remove('bg-brand-blue-0', 'rounded-l-full');
      bar.classList.add('bg-green-600', 'rounded-full');
      bar.style.width = '100%';
    }
    const check = row.querySelector('[data-role="check"]');
    if (check) check.classList.remove('hidden');

    this.restoreProgressOrder();
  }

  restoreProgressOrder() {
    if (!this.hasProgressListTarget) return;

    const rows = Array.from(this.progressListTarget.querySelectorAll('[data-progress-row]'));
    rows
      .sort((a, b) => Number(a.dataset.index) - Number(b.dataset.index))
      .forEach((row) => this.progressListTarget.appendChild(row));
    this.refreshProgressBorders();
  }

  refreshProgressBorders() {
    if (!this.hasProgressListTarget) return;
    const rows = Array.from(this.progressListTarget.querySelectorAll('[data-progress-row]'));
    rows.forEach((row, index) => {
      if (index === rows.length - 1) {
        row.classList.remove('border-b', 'border-gray-200');
      } else {
        row.classList.add('border-b', 'border-gray-200');
      }
    });
  }

  showIdleUi() {
    this.hideTarget('progressWrapper');

    if (this.entries.length > 0) {
      this.hideTarget('idlePanel');
      this.showTarget('explorerWrapper');
      this.renderExplorer();
      if (this.hasDownloadAllButtonTarget && this.downloadAllWasVisible) {
        this.downloadAllButtonTarget.classList.remove('hidden');
      }
    } else {
      this.hideTarget('explorerWrapper');
      this.showTarget('idlePanel');
    }

    if (this.hasUploadLabelTarget && !this.uploadLocked)
      this.uploadLabelTarget.classList.remove('hidden');
    if (this.hasUploadingLabelTarget) {
      this.uploadingLabelTarget.classList.add('hidden');
      this.uploadingLabelTarget.classList.remove('inline-flex');
    }
  }

  setProgressPhase(phase, current, total) {
    if (!this.hasProgressTotalLabelTarget) return;

    const template =
      phase === 'verifying'
        ? this.verifyingFilesTextValue || 'Verifying files %{current}/%{total}'
        : this.ofFilesTextValue || 'Uploading %{current}/%{total}';

    this.progressTotalLabelTarget.textContent = template
      .replace('%{current}', String(current))
      .replace('%{total}', String(total));
  }

  showExplorer() {
    this.hideTarget('progressWrapper');
    this.hideTarget('idlePanel');
    this.showTarget('explorerWrapper');

    if (this.hasUploadLabelTarget && !this.uploadLocked)
      this.uploadLabelTarget.classList.remove('hidden');
    if (this.hasUploadingLabelTarget) {
      this.uploadingLabelTarget.classList.add('hidden');
      this.uploadingLabelTarget.classList.remove('inline-flex');
    }
    if (this.hasDownloadAllButtonTarget) this.downloadAllButtonTarget.classList.remove('hidden');

    this.currentPath = '';
    this.renderExplorer();
  }

  entriesAt(path) {
    const prefix = path ? `${path}/` : '';
    const folders = new Set();
    const files = [];

    this.entries.forEach((entry) => {
      if (path) {
        if (entry.path === path || !entry.path.startsWith(prefix)) return;
      }

      const remainder = path ? entry.path.slice(prefix.length) : entry.path;
      if (!remainder) return;

      const slash = remainder.indexOf('/');
      if (slash === -1) {
        files.push({ ...entry, name: remainder });
      } else {
        folders.add(remainder.slice(0, slash));
      }
    });

    return {
      folders: Array.from(folders).sort((a, b) => a.localeCompare(b)),
      files: files.sort((a, b) => a.name.localeCompare(b.name)),
    };
  }

  renderExplorer() {
    if (!this.hasExplorerBodyTarget) return;

    const { folders, files } = this.entriesAt(this.currentPath);
    const rows = [];
    let index = 0;

    folders.forEach((folderName) => {
      const zebra = index % 2 === 1 ? 'bg-gray-50' : '';
      index += 1;
      const nextPath = this.currentPath ? `${this.currentPath}/${folderName}` : folderName;
      rows.push(`
        <button type="button"
                class="self-stretch ${zebra} inline-flex justify-center items-start w-full text-left hover:bg-gray-100"
                data-action="click->vdr-upload#openFolder"
                data-path="${this.escapeAttr(nextPath)}">
          <div class="flex-1 p-4 border-b border-gray-200 flex justify-start items-center gap-2">
            ${this.folderIconSvg()}
            <div class="justify-center text-brand-blue-0 text-sm font-semibold font-sans">${this.escapeHtml(
              folderName
            )}</div>
          </div>
          <div class="flex-1 p-4 border-b border-gray-200 inline-flex flex-col justify-start items-start">
            <div class="justify-center text-gray-800 text-sm font-normal font-sans">${this.escapeHtml(
              this.folderLabelValue || 'Folder'
            )}</div>
          </div>
          <div class="flex-1 p-4 border-b border-gray-200 inline-flex flex-col justify-start items-start">
            <div class="justify-center text-gray-800 text-sm font-normal font-sans">—</div>
          </div>
        </button>`);
    });

    files.forEach((file) => {
      const zebra = index % 2 === 1 ? 'bg-gray-50' : '';
      index += 1;
      rows.push(`
        <div class="self-stretch ${zebra} inline-flex justify-center items-start">
          <div class="flex-1 p-4 border-b border-gray-200 flex justify-start items-center gap-2">
            <div class="justify-center text-gray-800 text-sm font-semibold font-sans">${this.escapeHtml(
              file.name
            )}</div>
          </div>
          <div class="flex-1 p-4 border-b border-gray-200 inline-flex flex-col justify-start items-start">
            <div class="justify-center text-gray-800 text-sm font-normal font-sans">${this.escapeHtml(
              this.formatSize(file.size)
            )}</div>
          </div>
          <div class="flex-1 p-4 border-b border-gray-200 inline-flex flex-col justify-start items-start">
            <div class="justify-center text-gray-800 text-sm font-normal font-sans">${this.escapeHtml(
              file.uploadedAt
            )}</div>
          </div>
        </div>`);
    });

    this.explorerBodyTarget.innerHTML = rows.join('');
    this.renderBreadcrumb();
  }

  renderBreadcrumb() {
    if (!this.hasExplorerBreadcrumbTarget) return;

    const rootLabel = this.rootLabelValue || 'Files';
    const parts = this.currentPath ? this.currentPath.split('/').filter(Boolean) : [];
    const crumbs = [
      `<button type="button" class="font-semibold text-brand-blue-0 hover:underline" data-action="click->vdr-upload#goToPath" data-path="">${this.escapeHtml(
        rootLabel
      )}</button>`,
    ];

    let accum = '';
    parts.forEach((part, i) => {
      accum = accum ? `${accum}/${part}` : part;
      const isLast = i === parts.length - 1;
      if (isLast) {
        crumbs.push(
          `<span class="text-gray-400">/</span><span class="font-semibold text-gray-800">${this.escapeHtml(
            part
          )}</span>`
        );
      } else {
        crumbs.push(
          `<span class="text-gray-400">/</span><button type="button" class="font-semibold text-brand-blue-0 hover:underline" data-action="click->vdr-upload#goToPath" data-path="${this.escapeAttr(
            accum
          )}">${this.escapeHtml(part)}</button>`
        );
      }
    });

    this.explorerBreadcrumbTarget.innerHTML = crumbs.join('');
  }

  openFolder(event) {
    const path = event.currentTarget.dataset.path || '';
    this.currentPath = path;
    this.renderExplorer();
  }

  goToPath(event) {
    this.currentPath = event.currentTarget.dataset.path || '';
    this.renderExplorer();
  }

  folderIconSvg() {
    return `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5 text-brand-blue-0 shrink-0"><path stroke-linecap="round" stroke-linejoin="round" d="M2.25 12.75V12A2.25 2.25 0 0 1 4.5 9.75h15A2.25 2.25 0 0 1 21.75 12v.75m-8.69-6.44-2.12-2.12a1.5 1.5 0 0 0-1.061-.44H4.5A2.25 2.25 0 0 0 2.25 6v12a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18V9a2.25 2.25 0 0 0-2.25-2.25h-5.379a1.5 1.5 0 0 1-1.06-.44Z" /></svg>`;
  }

  showTarget(name) {
    const getter = `has${name.charAt(0).toUpperCase()}${name.slice(1)}Target`;
    const target = `${name}Target`;
    if (this[getter]) this[target].classList.remove('hidden');
  }

  hideTarget(name) {
    const getter = `has${name.charAt(0).toUpperCase()}${name.slice(1)}Target`;
    const target = `${name}Target`;
    if (this[getter]) this[target].classList.add('hidden');
  }

  basename(path) {
    const parts = String(path || '').split('/');
    return parts[parts.length - 1] || path;
  }

  formatSize(bytes) {
    const value = Number(bytes) || 0;
    if (value < 1024) return `${value} B`;
    if (value < 1024 * 1024) return `${Math.round(value / 1024)} KB`;
    return `${(value / (1024 * 1024)).toFixed(1)} MB`;
  }

  formatDate(date) {
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  }

  escapeHtml(value) {
    const div = document.createElement('div');
    div.textContent = value == null ? '' : String(value);
    return div.innerHTML;
  }

  escapeAttr(value) {
    return this.escapeHtml(value).replace(/"/g, '&quot;');
  }
}
